/**
 * Host Global - Inspector Dashboard
 * Operations team members can view their assigned inspections and property details
 * Enhanced with statistics, date filtering, and today's inspections highlight
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// API
import { inspectorAPI } from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

function InspectorDashboard() {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [filteredInspections, setFilteredInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [todayInspections, setTodayInspections] = useState([]);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    today: 0,
  });

  // Fetch assigned inspections
  useEffect(() => {
    fetchInspections();
  }, []);

  // Filter inspections based on status and date
  // Always filter from the original inspections list
  useEffect(() => {
    // Start with the original inspections list (not filteredInspections)
    let filtered = [...inspections];

    // Apply status filter first
    if (statusFilter !== "all") {
      if (statusFilter === "Pending") {
        // Pending / in progress: first and second inspection
        filtered = filtered.filter(
          (inspection) =>
            inspection.status === "Pending" ||
            inspection.status === "Inspection" ||
            inspection.status === "First_Inspection" ||
            inspection.status === "Second_Inspection"
        );
      } else if (statusFilter === "Inspection_Completed") {
        // Completed: first and second inspection completed, Approved, Rejected
        filtered = filtered.filter(
          (inspection) =>
            inspection.status === "Inspection_Completed" ||
            inspection.status === "Second_Inspection_Completed" ||
            inspection.status === "Approved" ||
            inspection.status === "Rejected"
        );
      } else {
        filtered = filtered.filter((inspection) => inspection.status === statusFilter);
      }
    }

    // Apply date filter (on the already status-filtered list)
    if (dateFilter) {
      const filterDate = new Date(dateFilter);
      filterDate.setHours(0, 0, 0, 0);
      const nextDay = new Date(filterDate);
      nextDay.setDate(nextDay.getDate() + 1);

      filtered = filtered.filter((inspection) => {
        if (!inspection.inspectionDate) return false;
        const inspectionDate = new Date(inspection.inspectionDate);
        return inspectionDate >= filterDate && inspectionDate < nextDay;
      });
    }

    setFilteredInspections(filtered);
  }, [statusFilter, dateFilter, inspections]);

  // Calculate today's inspections
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayList = inspections.filter((inspection) => {
      if (!inspection.inspectionDate) return false;
      const inspectionDate = new Date(inspection.inspectionDate);
      return (
        inspectionDate >= today &&
        inspectionDate < tomorrow &&
        ["Inspection", "Pending", "First_Inspection", "Second_Inspection"].includes(
          inspection.status
        )
      );
    });

    setTodayInspections(todayList);
  }, [inspections]);

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const response = await inspectorAPI.getAssignedInspections();
      const properties = response.properties || [];
      setInspections(properties);

      // Calculate statistics
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      setStats({
        total: properties.length,
        pending: properties.filter((p) =>
          ["Pending", "Inspection", "First_Inspection", "Second_Inspection"].includes(p.status)
        ).length,
        inProgress: 0,
        completed: properties.filter((p) =>
          ["Inspection_Completed", "Second_Inspection_Completed", "Approved", "Rejected"].includes(
            p.status
          )
        ).length,
        today: properties.filter((p) => {
          if (!p.inspectionDate) return false;
          const inspectionDate = new Date(p.inspectionDate);
          return (
            inspectionDate >= today &&
            inspectionDate < tomorrow &&
            ["Inspection", "Pending", "First_Inspection", "Second_Inspection"].includes(p.status)
          );
        }).length,
      });
    } catch (error) {
      console.error("Error fetching inspections:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Inspection":
      case "First_Inspection":
      case "Second_Inspection":
        return "warning";
      case "Inspection_Completed":
      case "Second_Inspection_Completed":
        return "success";
      case "Pending":
        return "warning";
      case "Approved":
        return "success";
      case "Rejected":
        return "error";
      default:
        return "secondary";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Inspection":
      case "First_Inspection":
      case "Second_Inspection":
        return "schedule";
      case "Inspection_Completed":
      case "Second_Inspection_Completed":
        return "check_circle";
      case "Pending":
        return "schedule";
      case "Approved":
        return "done";
      case "Rejected":
        return "cancel";
      default:
        return "help";
    }
  };

  // Get display status label - show "Inspection" as "Pending"
  const getStatusLabel = (status) => {
    if (status === "Inspection") {
      return "Pending";
    }
    return status.replace(/_/g, " ");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isToday = (dateString) => {
    if (!dateString) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const inspectionDate = new Date(dateString);
    return inspectionDate >= today && inspectionDate < tomorrow;
  };

  const isOverdue = (dateString, status) => {
    if (!dateString) return false;
    const completedStatuses = [
      "Inspection_Completed",
      "Second_Inspection_Completed",
      "Approved",
      "Rejected",
    ];
    if (completedStatuses.includes(status)) return false;
    if (!["Pending", "Inspection", "First_Inspection", "Second_Inspection"].includes(status))
      return false;
    const inspectionDate = new Date(dateString);
    const now = new Date();
    return inspectionDate < now;
  };

  const handleViewDetails = (propertyId) => {
    navigate(`/operations/inspections/${propertyId}`);
  };

  const clearDateFilter = () => {
    setDateFilter("");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold">
            My Inspections
          </MDTypography>
          <MDTypography variant="body2" color="text">
            View and manage your assigned property inspections
          </MDTypography>
        </MDBox>

        {/* Statistics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              title="Pending"
              count={stats.pending}
              icon="schedule"
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              title="Completed"
              count={stats.completed}
              icon="check_circle"
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              title="Today's Inspections"
              count={stats.today}
              icon="today"
              color="error"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              title="Total Inspections"
              count={stats.total}
              icon="assignment"
              color="info"
            />
          </Grid>
        </Grid>

        {/* Today's Inspections Section */}
        {todayInspections.length > 0 && (
          <MDBox mb={3}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
                color: "white",
                borderRadius: 2,
                boxShadow: 4,
              }}
            >
              <MDBox p={3}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ fontSize: "2rem" }}>event</Icon>
                    <MDTypography variant="h5" fontWeight="bold" color="white">
                      Today's Inspections ({todayInspections.length})
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <Grid container spacing={2}>
                  {todayInspections.map((property) => (
                    <Grid item xs={12} sm={6} md={4} key={property._id || property.id}>
                      <Paper
                        elevation={3}
                        sx={{
                          p: 2,
                          bgcolor: "rgba(255, 255, 255, 0.95)",
                          borderRadius: 2,
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: 6,
                          },
                        }}
                        onClick={() => handleViewDetails(property._id || property.id)}
                      >
                        <MDTypography variant="h6" fontWeight="bold" mb={1}>
                          {property.title?.en || property.title || "Untitled Property"}
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                          <Icon fontSize="small" color="error">
                            schedule
                          </Icon>
                          <MDTypography variant="body2" fontWeight="medium" color="error.main">
                            {formatTime(property.inspectionDate)}
                          </MDTypography>
                        </MDBox>
                        <MDBox mb={1} display="flex" gap={1}>
                          <MDButton
                            variant="gradient"
                            color="success"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(property._id || property.id);
                            }}
                            sx={{
                              flex: 1,
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: 2,
                              py: 0.75,
                              color: "white",
                              boxShadow: "0 2px 8px rgba(76, 175, 80, 0.2)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)",
                                transform: "translateY(-2px)",
                              },
                            }}
                            startIcon={<Icon>play_arrow</Icon>}
                          >
                            <MDTypography variant="button" color="white" fontWeight="medium">
                              Start Inspection
                            </MDTypography>
                          </MDButton>
                          <MDButton
                            variant="gradient"
                            color="info"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              const location = property.inspectionLocation || property.address;
                              if (location) {
                                const encodedLocation = encodeURIComponent(location);
                                window.open(
                                  `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`,
                                  "_blank"
                                );
                              }
                            }}
                            sx={{
                              minWidth: "auto",
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: 2,
                              py: 0.75,
                              px: 1.5,
                              color: "white",
                              boxShadow: "0 2px 8px rgba(33, 150, 243, 0.2)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(33, 150, 243, 0.4)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            <Icon>map</Icon>
                          </MDButton>
                        </MDBox>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
            </Card>
          </MDBox>
        )}

        {/* Filters */}
        <MDBox mb={3}>
          <Card>
            <MDBox p={2}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                    Filter by Status
                  </MDTypography>
                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      label="All"
                      onClick={() => setStatusFilter("all")}
                      color={statusFilter === "all" ? "primary" : "default"}
                      icon={<Icon>list</Icon>}
                      sx={{ cursor: "pointer" }}
                    />
                    <Chip
                      label="Pending"
                      onClick={() => setStatusFilter("Pending")}
                      color={statusFilter === "Pending" ? "primary" : "default"}
                      icon={<Icon>schedule</Icon>}
                      sx={{ cursor: "pointer" }}
                    />
                    <Chip
                      label="Completed"
                      onClick={() => setStatusFilter("Inspection_Completed")}
                      color={statusFilter === "Inspection_Completed" ? "primary" : "default"}
                      icon={<Icon>check_circle</Icon>}
                      sx={{ cursor: "pointer" }}
                    />
                    <Chip
                      label="Accepted"
                      onClick={() => setStatusFilter("Approved")}
                      color={statusFilter === "Approved" ? "primary" : "default"}
                      icon={<Icon>done</Icon>}
                      sx={{ cursor: "pointer" }}
                    />
                    <Chip
                      label="Declined"
                      onClick={() => setStatusFilter("Rejected")}
                      color={statusFilter === "Rejected" ? "primary" : "default"}
                      icon={<Icon>close</Icon>}
                      sx={{ cursor: "pointer" }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                    Filter by Date
                  </MDTypography>
                  <MDBox display="flex" gap={1} alignItems="center">
                    <TextField
                      type="date"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    {dateFilter && (
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={clearDateFilter}
                        startIcon={<Icon>clear</Icon>}
                      >
                        Clear
                      </MDButton>
                    )}
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </MDBox>

        {/* Inspections List */}
        {loading ? (
          <MDBox display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </MDBox>
        ) : filteredInspections.length === 0 ? (
          <Card>
            <MDBox p={6} textAlign="center">
              <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>inbox</Icon>
              <MDTypography variant="h6" color="text.secondary" mb={1}>
                No Inspections Found
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                {dateFilter || statusFilter !== "all"
                  ? "No inspections match your current filters."
                  : "You don't have any assigned inspections at the moment."}
              </MDTypography>
            </MDBox>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {filteredInspections.map((property) => {
              const overdue = isOverdue(property.inspectionDate, property.status);
              const today = isToday(property.inspectionDate);

              return (
                <Grid item xs={12} md={6} lg={4} key={property._id || property.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      border: today ? "2px solid" : overdue ? "2px solid" : "none",
                      borderColor: today
                        ? "success.main"
                        : overdue
                        ? "warning.main"
                        : "transparent",
                      bgcolor: today ? "rgba(76, 175, 80, 0.05)" : "transparent",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                    onClick={() => handleViewDetails(property._id || property.id)}
                  >
                    {/* Property Image */}
                    {property.images && property.images.length > 0 ? (
                      <MDBox
                        position="relative"
                        height="200px"
                        sx={{
                          backgroundImage: `url(${property.images[0]})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      >
                        {/* Status Badge */}
                        <MDBox position="absolute" top={12} right={12}>
                          <Chip
                            label={getStatusLabel(property.status)}
                            color={getStatusColor(property.status)}
                            size="small"
                            icon={<Icon>{getStatusIcon(property.status)}</Icon>}
                            sx={{ fontWeight: "bold" }}
                          />
                        </MDBox>
                        {/* Today/Overdue Badge */}
                        {today && (
                          <MDBox position="absolute" top={12} left={12}>
                            <Chip
                              label="Today"
                              color="success"
                              size="small"
                              icon={<Icon>today</Icon>}
                              sx={{ fontWeight: "bold" }}
                            />
                          </MDBox>
                        )}
                        {overdue && !today && (
                          <MDBox position="absolute" top={12} left={12}>
                            <Chip
                              label="Overdue"
                              color="warning"
                              size="small"
                              icon={<Icon>warning</Icon>}
                              sx={{ fontWeight: "bold" }}
                            />
                          </MDBox>
                        )}
                      </MDBox>
                    ) : (
                      <MDBox
                        height="200px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bgcolor="grey.200"
                        position="relative"
                      >
                        <Icon sx={{ fontSize: "3rem", color: "grey.400" }}>home</Icon>
                        {/* Status Badge */}
                        <MDBox position="absolute" top={12} right={12}>
                          <Chip
                            label={getStatusLabel(property.status)}
                            color={getStatusColor(property.status)}
                            size="small"
                            icon={<Icon>{getStatusIcon(property.status)}</Icon>}
                            sx={{ fontWeight: "bold" }}
                          />
                        </MDBox>
                        {/* Today/Overdue Badge */}
                        {today && (
                          <MDBox position="absolute" top={12} left={12}>
                            <Chip
                              label="Today"
                              color="success"
                              size="small"
                              icon={<Icon>today</Icon>}
                              sx={{ fontWeight: "bold" }}
                            />
                          </MDBox>
                        )}
                        {overdue && !today && (
                          <MDBox position="absolute" top={12} left={12}>
                            <Chip
                              label="Overdue"
                              color="warning"
                              size="small"
                              icon={<Icon>warning</Icon>}
                              sx={{ fontWeight: "bold" }}
                            />
                          </MDBox>
                        )}
                      </MDBox>
                    )}

                    <MDBox p={3} flex={1} display="flex" flexDirection="column">
                      {/* Property Title */}
                      <MDTypography variant="h6" fontWeight="bold" mb={1}>
                        {property.title?.en || property.title || "Untitled Property"}
                      </MDTypography>

                      {/* Property Details */}
                      <MDBox mb={2} flex={1}>
                        <MDBox mb={1}>
                          <MDButton
                            variant="gradient"
                            color="info"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              const addressValue = property.address?.en || property.address || "";
                              const cityValue = property.city?.en || property.city || "";
                              const location =
                                property.inspectionLocation ||
                                addressValue ||
                                (cityValue ? `${addressValue}, ${cityValue}` : addressValue);
                              if (location) {
                                const encodedLocation = encodeURIComponent(location);
                                window.open(
                                  `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`,
                                  "_blank"
                                );
                              }
                            }}
                            fullWidth
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: 2,
                              py: 0.75,
                              color: "white",
                              boxShadow: "0 2px 8px rgba(33, 150, 243, 0.2)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(33, 150, 243, 0.4)",
                                transform: "translateY(-2px)",
                              },
                            }}
                            startIcon={<Icon>map</Icon>}
                          >
                            <MDTypography variant="button" color="white" fontWeight="medium">
                              Open in Google Maps
                            </MDTypography>
                          </MDButton>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" mb={0.5}>
                          <Icon fontSize="small" sx={{ mr: 1, color: "text.secondary" }}>
                            category
                          </Icon>
                          <MDTypography variant="body2" color="text">
                            {property.propertyType
                              ? property.propertyType.charAt(0).toUpperCase() +
                                property.propertyType.slice(1)
                              : "N/A"}{" "}
                            • {property.rooms} rooms • {property.bathrooms} baths
                          </MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" mb={0.5}>
                          <Icon fontSize="small" sx={{ mr: 1, color: "text.secondary" }}>
                            person
                          </Icon>
                          <MDTypography variant="body2" color="text">
                            Owner: {property.owner?.fullName || "N/A"}
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          mb={0.5}
                          sx={{
                            color: today
                              ? "success.main"
                              : overdue
                              ? "warning.main"
                              : "text.secondary",
                            fontWeight: today || overdue ? "bold" : "normal",
                          }}
                        >
                          <Icon fontSize="small" sx={{ mr: 1 }}>
                            calendar_today
                          </Icon>
                          <MDTypography variant="body2">
                            {formatDate(property.inspectionDate)}
                          </MDTypography>
                        </MDBox>
                      </MDBox>

                      {/* Action Button */}
                      <MDButton
                        variant="gradient"
                        color={today ? "success" : overdue ? "warning" : "info"}
                        size="medium"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(property._id || property.id);
                        }}
                        startIcon={<Icon>visibility</Icon>}
                      >
                        {property.status === "Inspection_Completed"
                          ? "View Details"
                          : property.status === "Inspection"
                          ? "Start Inspection"
                          : "View Details"}
                      </MDButton>
                    </MDBox>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InspectorDashboard;
